import React, { useEffect } from 'react';
import Footer from '../../components/Footer';
import nvidiaBadge from '../../assets/Newsletter/Nvidia.png';
import './Nvidia.css';

const ArrowIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M13 5l7 7-7 7" />
  </svg>
);

const Nvidia = () => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Vardaan Data Sciences Joins the NVIDIA Inception Program';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="nvidia-inception-page">
      <header className="nv-hero">
        <div className="nv-container">
          <div className="nv-hero-grid">
            <div>
              <div className="nv-eyebrow">
                <span className="nv-dot" />
                <span className="nv-accent program-name">NVIDIA Inception Program</span>
                <span className="nv-pipe">·</span>
                2026
              </div>

              <h1 className="nv-hero-title">
                Advancing AI for the most <em>regulated industries.</em>
              </h1>

              <p className="nv-hero-lede">
                Vardaan Data Sciences is now a member of the NVIDIA Inception program — a program from NVIDIA that supports startups building advanced AI products. Our membership reflects our continued focus on AI innovation and the ongoing development of RiskaVaire, our Governance, Risk and Compliance platform, and gives Vardaan access to NVIDIA&apos;s developer tools, infrastructure resources, technical training, and global ecosystem of investors and partners.
              </p>

              <div className="nv-hero-actions">
                <a href="#announcement" className="nv-btn nv-btn-primary">
                  Read the announcement
                  <ArrowIcon />
                </a>
                <a href="#architecture" className="nv-btn nv-btn-secondary">See the architecture</a>
              </div>
            </div>

            <aside className="nv-member-card" aria-label="NVIDIA Inception Program membership">
              <img
                className="nv-member-badge"
                src={nvidiaBadge}
                alt="NVIDIA Inception Program"
              />
              <div className="nv-member-card-eyebrow">Program Member</div>
              <div className="nv-member-card-divider" />
              <div className="nv-member-card-statement">Member of the</div>
              <div className="nv-member-card-program program-name">NVIDIA Inception program</div>
              <div className="nv-member-card-company">— Vardaan Data Sciences</div>
              <div className="nv-member-card-foot">Member Since 2026</div>
            </aside>
          </div>
        </div>
      </header>

      <section id="announcement" className="nv-section nv-section-announcement">
        <div className="nv-container">
          <div className="nv-section-header">
            <div className="nv-section-num">
              <span className="nv-roman">i.</span>
              The Announcement
            </div>
            <h2 className="nv-section-title">Building AI-native governance and risk solutions.</h2>
          </div>

          <div className="nv-body-grid">
            <div className="nv-body-meta">
              Hyderabad · India
              <br />
              2026
            </div>

            <div className="nv-body-text">
              <p>
                Vardaan Data Sciences is excited to announce its membership in the NVIDIA Inception program — a program from NVIDIA that supports startups building advanced AI products and helps them grow through access to cutting-edge technology, technical training, and a global ecosystem of investors and partners. Our membership reflects our continued focus on AI innovation and the ongoing development of RiskaVaire, our Governance, Risk and Compliance (GRC) and Third-Party Risk Management (TPRM) platform. For Vardaan, joining the NVIDIA Inception program is a meaningful step in scaling the AI capabilities that sit at the heart of our enterprise platforms.
              </p>

              <p>
                Our application to NVIDIA Inception highlighted the product work behind RiskaVaire and our approach to building AI-powered governance and risk solutions. RiskaVaire leverages technologies including NVIDIA NIM for accelerated language-model inference, NVIDIA NeMo Retriever for rapid document extraction and semantic retrieval, and NVIDIA NeMo Guardrails for source-grounded compliance responses. The result is a platform engineered by Vardaan for the specific demands of governance and risk work — speed, accuracy, and auditability in the same workflow.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="architecture" className="nv-section nv-section-architecture">
        <div className="nv-container">
          <div className="nv-section-header">
            <div className="nv-section-num">
              <span className="nv-roman">ii.</span>
              The Architecture
            </div>
            <h2 className="nv-section-title">An AI stack engineered for trust and speed.</h2>
          </div>

          <div className="nv-body-grid">
            <div className="nv-body-meta">
              RiskaVaire
              <br />
              AI Engineering
              <br />
              2026 Build
            </div>

            <div className="nv-body-text">
              <p>
                RiskaVaire is built by Vardaan to automate the most demanding parts of governance, compliance, and risk operations — work that has historically depended on manual evidence collection, periodic vendor assessments, and disconnected risk indicators. The platform leverages three NVIDIA technologies in a single, cohesive pipeline designed for compliance-grade AI. Each component does one job exceptionally well, and together they produce the speed-with-trust profile that GRC workflows actually require.
              </p>

              <div className="nv-tech-stack" role="list" aria-label="NVIDIA technologies leveraged by RiskaVaire">
                <article className="nv-tech-card" role="listitem">
                  <div className="nv-tech-card-label">Inference</div>
                  <div className="nv-tech-card-name">NVIDIA NIM</div>
                  <div className="nv-tech-card-desc">High-performance, accelerated language-model inference at production scale.</div>
                </article>

                <article className="nv-tech-card" role="listitem">
                  <div className="nv-tech-card-label">Retrieval</div>
                  <div className="nv-tech-card-name">NVIDIA NeMo Retriever</div>
                  <div className="nv-tech-card-desc">Rapid document extraction and semantic retrieval across regulatory and evidence corpora.</div>
                </article>

                <article className="nv-tech-card" role="listitem">
                  <div className="nv-tech-card-label">Governance</div>
                  <div className="nv-tech-card-name">NVIDIA NeMo Guardrails</div>
                  <div className="nv-tech-card-desc">Source-grounded, policy-aware responses that keep compliance outputs auditable.</div>
                </article>
              </div>

              <p className="nv-body-text-spaced">
                These technologies are applied by Vardaan to the workflows that matter most in regulated environments — regulatory intelligence and obligation tracking, evidence validation and audit support, vendor due diligence and continuous TPRM monitoring, control assessments, risk indicator surfacing, and automated reporting. The objective is straightforward: shift the work of risk and compliance teams from gathering and reconciling information to making decisions, with AI that can be trusted because its sources can be traced.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="nv-section nv-section-vardaan">
        <div className="nv-container">
          <div className="nv-section-header">
            <div className="nv-section-num">
              <span className="nv-roman">iii.</span>
              Where This Fits
            </div>
            <h2 className="nv-section-title">An early chapter in a longer commitment.</h2>
          </div>

          <div className="nv-body-grid">
            <div className="nv-body-meta">
              Vardaan Data Sciences
              <br />
              Founded 2023
              <br />
              Hyderabad &amp; Dubai
            </div>

            <div className="nv-body-text">
              <p>
                Vardaan Data Sciences operates at the intersection of artificial intelligence, governance, risk and compliance, third-party risk management, cybersecurity, identity and access, enterprise automation, analytics, and operational intelligence. Our platforms, including RiskaVaire GRC and RiskaVaire TPRM alongside our broader AI-enabled transformation services, are built for organisations operating under significant regulatory pressure — by a team whose technical depth in AI is matched by deep domain experience inside large, regulated enterprises.
              </p>

              <div className="nv-industry-pills" aria-label="Industries served">
                <span className="nv-industry-pill">Banking &amp; FS</span>
                <span className="nv-industry-pill">Insurance</span>
                <span className="nv-industry-pill">Healthcare</span>
                <span className="nv-industry-pill">Manufacturing</span>
                <span className="nv-industry-pill">Pharmaceuticals</span>
                <span className="nv-industry-pill">Government</span>
                <span className="nv-industry-pill">Critical Infrastructure</span>
              </div>

              <p className="nv-body-text-spaced-sm">
                What we are building is larger than any single product category. Vardaan is working toward what we believe will be one of the defining categories of enterprise software this decade — the <em>trust and intelligence layer</em> that lets modern organisations stay accountable, resilient, and decisive at the speed AI demands. Joining the NVIDIA Inception program is part of how we accelerate that ambition — by giving our engineering teams direct access to the infrastructure and resources that make production-grade enterprise AI possible.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="nv-section nv-section-road">
        <div className="nv-container">
          <div className="nv-section-header">
            <div className="nv-section-num">
              <span className="nv-roman">iv.</span>
              The Road Ahead
            </div>
            <h2 className="nv-section-title">What this membership opens up.</h2>
          </div>

          <div className="nv-body-grid">
            <div className="nv-body-meta">
              Program Access
              <br />
              2026 onward
            </div>

            <div className="nv-body-text">
              <p>
                As a member of the NVIDIA Inception program, Vardaan gains access to the latest NVIDIA SDKs, model libraries, and developer platforms; preferred pricing on NVIDIA hardware and software, including NVIDIA AI Enterprise; technical training through the NVIDIA Deep Learning Institute; cloud credits across NVIDIA&apos;s partner ecosystem; and exposure to a global ecosystem of investors and AI-focused organisations. We plan to use this access to accelerate the inference, retrieval, and reasoning workloads that power RiskaVaire and our broader platforms — and to deepen the AI engineering practices that produced them.
              </p>

              <div className="nv-benefit-grid" role="list" aria-label="Membership benefits">
                <article className="nv-benefit" role="listitem">
                  <div className="nv-benefit-num">01</div>
                  <h4>Developer Tools &amp; SDKs</h4>
                  <p>Access to the latest NVIDIA model libraries and developer platforms for AI engineering.</p>
                </article>

                <article className="nv-benefit" role="listitem">
                  <div className="nv-benefit-num">02</div>
                  <h4>Cloud &amp; Compute</h4>
                  <p>Partner-ecosystem cloud credits for GPU instances to support training and inference workloads.</p>
                </article>

                <article className="nv-benefit" role="listitem">
                  <div className="nv-benefit-num">03</div>
                  <h4>Hardware &amp; Software</h4>
                  <p>Preferred pricing on selected NVIDIA hardware and the NVIDIA AI Enterprise suite.</p>
                </article>

                <article className="nv-benefit" role="listitem">
                  <div className="nv-benefit-num">04</div>
                  <h4>Technical Training</h4>
                  <p>Self-paced courses and expert-led workshops through the NVIDIA Deep Learning Institute.</p>
                </article>

                <article className="nv-benefit" role="listitem">
                  <div className="nv-benefit-num">05</div>
                  <h4>Investor Access</h4>
                  <p>Eligibility for Inception Capital Connect and exposure to NVIDIA&apos;s venture capital network.</p>
                </article>

                <article className="nv-benefit" role="listitem">
                  <div className="nv-benefit-num">06</div>
                  <h4>Ecosystem Exposure</h4>
                  <p>Co-marketing opportunities and visibility across the NVIDIA Inception member ecosystem.</p>
                </article>
              </div>

              <blockquote className="nv-pull-quote">
                &ldquo;For Vardaan, this membership is operational, not symbolic — direct access to the resources that help us build production-grade AI for the parts of the enterprise that most need it.&rdquo;
              </blockquote>

              <p>
                The path forward is clear. Vardaan will continue to invest in AI-native products across GRC, TPRM, cybersecurity, and operational intelligence, and use the NVIDIA Inception program to move from disciplined prototypes to production-grade systems faster. Our objective is to build AI that is fast, governed, and grounded — AI that earns the trust of the most regulated industries in the world, and that does so at the speed those industries now expect.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="nv-section nv-section-about">
        <div className="nv-container">
          <div className="nv-section-header">
            <div className="nv-section-num">
              <span className="nv-roman">v.</span>
              About the Program
            </div>
            <h2 className="nv-section-title">About the NVIDIA Inception Program.</h2>
          </div>

          <div className="nv-about-card">
            <p>
              NVIDIA Inception is a free program designed to help startups evolve faster through cutting-edge technology, access to the latest technical resources from NVIDIA, and connections with venture capitalists. The program supports members at every stage of growth, from prototype through production deployment, and has no application fees, no deadlines, and no cohorts.
            </p>
            <p>
              Members receive a customised set of ongoing benefits, including credits for self-paced courses through the NVIDIA Deep Learning Institute, preferred pricing on selected NVIDIA hardware and software, partner cloud credits, co-marketing support, and exposure to a global ecosystem of investors and AI-focused organisations. Learn more about the NVIDIA Inception program at{' '}
              <a href="https://www.nvidia.com/en-us/startups/" target="_blank" rel="noopener noreferrer">nvidia.com/startups</a>.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Nvidia;
